import type { IndustryPage } from "@/lib/data";

export const paintersDecorators: IndustryPage = {
  slug: "painters-decorators",
  seoTitle:
    "Painter and Decorator Websites UK | £1,000, Live in 7 Days",
  seoDescription:
    "Painter and decorator websites with before-and-after galleries, colour consultation booking and local SEO. Fixed £1,000, live in 7 days. Book a call.",
  serviceName: "Website design for painters and decorators",
  tldr:
    "ConstructionWebDesign.uk builds painter and decorator websites for UK decorating firms and sole traders: mobile-first sites with before-and-after galleries, a page for each type of work and a photo quote form. The price is £1,000, one-off and fixed, with no subscription. Your website goes live in 7 days from the discovery call, and you own it.",
  pricingFromGBP: 1000,
  hero: {
    eyebrow: "Painter and decorator websites · UK",
    headline: "Website design for painters and decorators that sells the finish.",
    highlight: "sells the finish",
    sub: "Painter and decorator websites with a before-and-after slider on every job, a page for each type of decorating, colour consultation booking, a photo-first quote form and the trade credentials you hold.",
    secondaryCta: { label: "See decorator demos", href: "#demos" },
  },
  slides: [
    {
      bg: "https://constructionwebdesign.cdn.modulify.website/images/hero/loft_conversion_slider_ratio.jpg",
      alt: "Interior room mid-conversion, at the stage where the decorating starts",
      headlineTop: "Sell the finish,",
      headlineBottom: "not the day rate.",
      preview: "https://constructionwebdesign.cdn.modulify.website/images/demo-fullheight/renovia.jpg",
    },
  ],
  demoSlugs: ["renovia", "halden"],
  demosHeading: "Painter and decorator website demo templates.",
  demosSub:
    "The closest demo templates to decorating work, not client work. Open either one to see how a before-and-after gallery and a room-by-room service list read to a homeowner.",
  value: {
    eyebrow: "Why it matters",
    heading: "Why does a painter and decorator need a website?",
    intro:
      "A painter and decorator needs a website because customers can't judge a finish until the job is done, so they judge on photos of finished rooms. A decorator website shows your before-and-after work, how you protect a home and the trade schemes you belong to, and takes enquiries while you're on a job.",
    points: [
      {
        title: "Show the finish, close up.",
        body: "Cut-in lines, straight edges against coving, a sprayed door that looks factory-finished. Close-up photos show the quality of decorating work in a way a wide shot of a painted room can't, and they give a homeowner a reason to choose you over a cheaper day-rate quote.",
        icon: "Brush",
      },
      {
        title: "Let before-and-after photos do the selling.",
        body: "A tired hallway next to the finished one says more than any paragraph of copy. Each job on a decorator website can show a before-and-after pair with the room, the products used, how long it took and whether the customer stayed in the house during the work.",
        icon: "Sparkle",
      },
      {
        title: "Separate pages for domestic and commercial work.",
        body: "Homeowners want colour advice and a tidy job. Landlords, letting agents and facilities managers want void turnarounds, out-of-hours working and a price per room. Same trade, different buyers and different searches, so each gets its own pages.",
        icon: "Buildings",
      },
      {
        title: "Show you'll leave the house clean.",
        body: "Dust sheets, masked edges and a daily tidy-up matter to anyone letting a decorator into their home. Photos of a protected room mid-job make the point. Your public liability insurance, Dulux Select Decorators status or PDA membership sits near the top of the page, shown only if you hold it.",
        icon: "Shield",
      },
    ],
  },
  features: {
    eyebrow: "What's in the site",
    heading: "What does decorator website design include?",
    intro:
      "Decorator website design from ConstructionWebDesign.uk includes a before-and-after slider on every job, a page for each type of decorating, colour consultation booking, a tidy-house section, the trade credentials you hold and a photo-first quote form. Every site is mobile-first, with on-page local SEO for the towns you cover.",
    items: [
      {
        title: "A before-and-after slider on every job.",
        body: "Drag-to-reveal photos taken from the same camera angle, with the room type, the products used, days on site and whether the customer lived in during the work. Each job gets its own page, so every finished room adds another description of your work and the town it's in.",
      },
      {
        title: "A page for each type of decorating.",
        body: "Interior painting, exterior masonry and render, wallpapering, spray finishing, staircases and spindles, kitchen cabinet respraying and commercial voids each get their own page. Customers search for the job, such as \"kitchen respray\" or \"wallpaper hanger\" plus their town, rather than for decorating in general.",
      },
      {
        title: "Heritage and period property work.",
        body: "A separate page for the heritage work you take on: limewash and breathable mineral paints, sash windows, original plaster and mouldings, and jobs on listed buildings where consent may be needed. Owners of older houses look for a decorator who understands that work, so it gets its own page.",
      },
      {
        title: "Colour consultation booking.",
        body: "A homeowner stuck between three tester patches can book a colour visit from a short form. It turns a browsing visitor into an appointment without a phone call, and the page says whether the visit is free or charged.",
      },
      {
        title: "The tidy-house section.",
        body: "Dust sheets, masking, protected floors, a daily clean-down, working around pets and children, and what time you start in the morning. Each point is shown with photos taken mid-job rather than written as a promise.",
      },
      {
        title: "Trade credentials near the top of the page.",
        body: "Dulux Select Decorators, Painting and Decorating Association (PDA) membership, TrustMark, CSCS cards for site work, public liability insurance and DBS checks for school or care work, with membership numbers linked to the official register where one exists. If you don't hold a scheme, its badge stays off the site.",
      },
      {
        title: "A photo-first quote form.",
        body: "The customer photographs the room and adds the postcode, the ceiling height and whether they want walls, woodwork or the lot. You see the job before you call back, so you can price straightforward work from the photos and save site visits for the jobs that need one.",
      },
    ],
  },
  faqs: [
    {
      q: "How much does a painter and decorator website cost in the UK?",
      a: "A painter and decorator website from ConstructionWebDesign.uk costs £1,000, a one-off fixed price with no subscription. The Website + Brand package costs £1,399 and adds a logo, visual identity and business card mockups. For comparison, typical UK ranges are about £4,000–£5,000 for a freelancer and £5,000–£25,000 for an agency, while Wix or Squarespace cost about £22 a month. You own the finished website.",
    },
    {
      q: "How long does it take to build a decorator website?",
      a: "A decorator website from ConstructionWebDesign.uk goes live in 7 days from the discovery call. The work runs in five steps: discovery call, strategy and sitemap, design, build, then launch and support. Having before-and-after photos, your list of services and any PDA or Dulux Select Decorators details ready for the call keeps the week on track.",
    },
    {
      q: "What should a painter and decorator website include?",
      a: "A painter and decorator website should include before-and-after photos of every job, a page for each type of work (interiors, exteriors, wallpapering, spraying, heritage and commercial), colour consultation booking, a photo quote form, a one-tap call button, reviews and the trade schemes you belong to. The site should also show how you protect a customer's home, and work well on a phone.",
    },
    {
      q: "Do painters and decorators need a website?",
      a: "Yes, painters and decorators need a website, even as sole traders. Customers can't judge a decorator's finish until the job is done, so they look for photos of finished rooms, reviews and proof of insurance before they ask for a quote. A website keeps all of that in one place, with the areas you cover, instead of leaving customers to judge you on price alone.",
    },
    {
      q: "How do websites for decorators get found on Google?",
      a: "Websites for decorators get found on Google through on-page local SEO, a separate page for each service and area, a complete Google Business Profile and steady customer reviews. Every decorator website from ConstructionWebDesign.uk includes on-page local SEO, so page titles, headings and copy name your services and the towns you cover. Nobody can honestly guarantee a ranking, but that structure gives Google clear signals about what you do and where.",
    },
    {
      q: "How should a decorator photograph before-and-after work?",
      a: "A decorator should shoot the before photo from a fixed spot, such as a corner of the room with the phone at chest height, then shoot the after from exactly the same spot. Use natural light with the blinds open and no ladders in shot, and add one close-up of a cut-in edge or a sprayed door. Those photos become the gallery on your decorator website.",
    },
    {
      q: "Should a painter and decorator show prices on their website?",
      a: "Yes, a painter and decorator can show typical price ranges rather than fixed prices. Ranges for a box room, a hallway, stairs and landing or a three-bed exterior help customers check their budget without committing you to a figure before you've seen the walls. On a ConstructionWebDesign.uk decorator website, the figures and wording are yours, and nothing goes live without your approval.",
    },
    {
      q: "Can a decorator website bring in commercial and landlord work?",
      a: "Yes, a decorator website can target commercial and landlord work by giving it separate pages. Void turnarounds for letting agents, out-of-hours office decorating, HMO and student-let refreshes, and school work with DBS-checked staff are different searches with different buyers. Web design for decorators from ConstructionWebDesign.uk gives each of them its own page rather than a line on the homepage.",
    },
  ],
  guides: [
    {
      href: "/blog/how-much-should-a-construction-website-cost",
      label: "How much does a construction website cost in the UK?",
    },
    {
      href: "/blog/how-to-get-more-construction-leads",
      label: "How to get more construction leads without paying",
    },
    {
      href: "/blog/google-business-profile-for-builders",
      label: "Google Business Profile for builders: setup guide",
    },
    {
      href: "/blog/online-presence-construction-company",
      label: "Online presence for construction companies: a guide",
    },
  ],
  finalCta: {
    headline: "Talk to us about your decorating website.",
    highlight: "your decorating website",
    sub: "Book a free 30-minute call to talk through your decorating work and the areas you cover. Fixed £1,000 price, and your website goes live in 7 days.",
  },
};
